import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  CalendarCheck,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  FileHeart,
  HeartPulse,
  Mars,
  Mic,
  Navigation,
  Pause,
  Pill,
  Play,
  RotateCcw,
  Square,
  UserCheck,
  Users,
  Venus,
  Volume2,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { axiosAssistant, axiosEvents } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import SectionHeader from "../../guided/components/Shared/SectionHeader.jsx";
import GuidedFlowRenderer from "../../guided/components/GuidedFlow/GuidedFlowRenderer.jsx";
import {
  PATIENT_BOOK_APPOINTMENT,
  PATIENT_CANCEL_APPOINTMENT,
  PATIENT_RESCHEDULE_APPOINTMENT,
} from "../../guided/constants/guidedActionIds.js";
import {
  cancelGuidedFlow,
  continueGuidedFlow,
  startGuidedFlow,
} from "../../guided/services/guidedAssistantApi.js";
import "./AgentIA.css";

const trimUrl = (value) => String(value || "").replace(/\/+$/, "");

const VOICE_CHAT_ENDPOINTS = (() => {
  const explicitVoiceUrl = trimUrl(import.meta.env.VITE_ASSISTANT_VOICE_CHAT_URL);

  const directVoiceBaseUrl = trimUrl(
    import.meta.env.VITE_ASSISTANT_VOICE_BASE_URL ||
    import.meta.env.VITE_ASSISTANT_DIRECT_URL
  );

  return Array.from(new Set([
    ENDPOINTS.ASSISTANT.VOICE_CHAT,
    explicitVoiceUrl,
    directVoiceBaseUrl ? `${directVoiceBaseUrl}/v1/voice/chat` : "",
  ].filter(Boolean)));
})();
const MAX_RECORDING_MS = 30000;
const MICROPHONE_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const isInsecureBrowserContext = () =>
  typeof window !== "undefined" && window.isSecureContext === false;

const showBrowserAlert = (message) => {
  if (typeof window !== "undefined" && typeof window.alert === "function") {
    window.alert(message);
  }
};

const getSpeechRecognitionConstructor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const browserSpeechLanguage = (locale) => {
  if (locale === "ar") return "ar-TN";
  if (locale === "en") return "en-US";
  return "fr-FR";
};

const normalizeLocale = (locale) => {
  const value = String(locale || "").toLowerCase();
  if (value.startsWith("ar")) return "ar";
  return value.startsWith("en") ? "en" : "fr";
};

const eventCancelText = (locale) => ({
  fr: {
    cancel: "Annuler", confirm: "Confirmer", back: "Retour", working: "Annulation en cours…",
    prompt: "Annuler cet événement ?", forbidden: "Vous n'êtes pas autorisé à annuler cet événement.",
    missing: "Cet événement est introuvable. Actualisez la liste.",
    conflict: "L'état de cet événement a changé. Actualisez la liste.",
    timeout: "La réponse est incertaine. Actualisez la liste avant de réessayer.",
    service: "Le service des événements est indisponible (HTTP 502/503). Réessayez après vérification du service.",
    network: "Connexion au service des événements impossible. Vérifiez le réseau et l'adresse du service.",
    unavailable: "L'annulation est indisponible pour le moment.",
    refresh: "L'annulation a été acceptée, mais la liste n'a pas pu être actualisée.",
    list: (count) => `${count} événement${count > 1 ? "s" : ""} après actualisation.`,
  },
  en: {
    cancel: "Cancel", confirm: "Confirm", back: "Back", working: "Cancelling…",
    prompt: "Cancel this event?", forbidden: "You are not allowed to cancel this event.",
    missing: "This event was not found. Refresh the list.",
    conflict: "This event has changed. Refresh the list.",
    timeout: "The result is uncertain. Refresh the list before trying again.",
    service: "The events service is unavailable (HTTP 502/503). Check the service before trying again.",
    network: "Cannot connect to the events service. Check the network and service address.",
    unavailable: "Cancellation is temporarily unavailable.",
    refresh: "Cancellation was accepted, but the list could not be refreshed.",
    list: (count) => `${count} event${count === 1 ? "" : "s"} after refresh.`,
  },
  ar: {
    cancel: "إلغاء", confirm: "تأكيد", back: "رجوع", working: "جارٍ الإلغاء…",
    prompt: "هل تريد إلغاء هذا الحدث؟", forbidden: "ليس لديك صلاحية إلغاء هذا الحدث.",
    missing: "هذا الحدث غير موجود. حدّث القائمة.",
    conflict: "تغيّرت حالة هذا الحدث. حدّث القائمة.",
    timeout: "النتيجة غير مؤكدة. حدّث القائمة قبل المحاولة مجدداً.",
    service: "خدمة الأحداث غير متاحة (HTTP 502/503). تحقق من الخدمة قبل إعادة المحاولة.",
    network: "تعذر الاتصال بخدمة الأحداث. تحقق من الشبكة وعنوان الخدمة.",
    unavailable: "الإلغاء غير متاح حالياً.",
    refresh: "تم قبول الإلغاء، لكن تعذّر تحديث القائمة.",
    list: (count) => `${count} حدث بعد التحديث.`,
  },
})[normalizeLocale(locale)];

const structuredEventId = (event) => {
  const id = Number(event?.id ?? event?._id ?? event?.eventId);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const eventsInOriginalRange = (events, data) => {
  const start = Date.parse(data?.start || "");
  const end = Date.parse(data?.end || "");
  if (!Number.isFinite(start) || !Number.isFinite(end)) return events;
  return events.filter((event) => {
    const eventStart = Date.parse(event?.startTime || event?.start || "");
    return Number.isFinite(eventStart) && eventStart >= start && eventStart < end;
  });
};

const eventDeleteFailure = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return "forbidden";
  if (status === 404) return "missing";
  if (status === 409) return "conflict";
  if (error?.code === "ECONNABORTED" || error?.name === "TimeoutError") return "timeout";
  if (status === 502 || status === 503) return "service";
  if (!status) return "network";
  return "unavailable";
};

const readI18nObject = (t, key, lng) => {
  const value = t(key, { lng, returnObjects: true });
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
};

const normalizeLabelKey = (value) => (
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
);

const displayLinkLabel = (value, copy) => {
  const raw = String(value || "").trim();
  if (!raw) return copy.open;
  const dictionary = copy.linkLabels || {};
  return dictionary[normalizeLabelKey(raw)] || dictionary[raw.toLowerCase()] || raw;
};

const MEDICAL_ASSISTANT_INTERNAL_ACTION = "open_patient_health_assistant";

const normalizeMedicalRedirectText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const isMedicalAssistantRedirectMessage = (message) => {
  if (
    message?.meta?.medical_assistant_redirect === true ||
    message?.meta?.medical_assistant_action === MEDICAL_ASSISTANT_INTERNAL_ACTION ||
    message?.data?.assistant_internal_action === MEDICAL_ASSISTANT_INTERNAL_ACTION
  ) {
    return true;
  }

  const normalizedText = normalizeMedicalRedirectText(message?.text);
  return (
    (
      normalizedText.includes("cette question concerne la sante") &&
      normalizedText.includes("assistant sante")
    ) ||
    (
      normalizedText.includes("this question is about health") &&
      normalizedText.includes("health assistant")
    )
  );
};

const getMedicalAssistantRedirectLabel = (message, locale) => {
  const fallback =
    locale === "en"
      ? "Health assistant"
      : locale === "ar"
        ? "\u0645\u0633\u0627\u0639\u062f \u0627\u0644\u0635\u062d\u0629"
        : "Assistant sante";
  return (
    message?.meta?.medical_assistant_button_label ||
    message?.data?.assistant_button_label ||
    fallback
  );
};

const GUIDE_ICONS = {
  appointments: CalendarDays,
  availability: CalendarCheck,
  patients: Users,
  waiting_room: UserCheck,
  events: Bell,
  medical_record: FileHeart,
  medications: Pill,
  locations: MapPin,
  navigation: Navigation,
  assistant: HeartPulse,
};

const formatGuideCount = (copy, count) => (
  String(copy.guideActionCount || "{{count}} actions")
    .replace("{{count}}", String(count))
);

const getAgentIACopy = (t, locale) => {
  const fallback = readI18nObject(t, "agentIA", "fr");
  const translated = readI18nObject(t, "agentIA", locale);
  return {
    ...fallback,
    ...translated,
    steps: Array.isArray(translated.steps) ? translated.steps : fallback.steps || [],
    cards: {
      ...(fallback.cards || {}),
      ...(translated.cards || {}),
    },
    linkLabels: {
      ...(fallback.linkLabels || {}),
      ...(translated.linkLabels || {}),
    },
  };
};

const postVoiceChat = async (formData) => {
  let lastError = null;

  for (const endpoint of VOICE_CHAT_ENDPOINTS) {
    try {
      if (/^https?:\/\//i.test(endpoint)) {
        const response = await fetch(endpoint, {
          method: "POST",
          body: formData,
          credentials: "omit",
        });

        if (!response.ok) {
          const error = new Error(`Voice endpoint failed with ${response.status}`);
          error.response = { status: response.status };
          throw error;
        }

        return { data: await response.json() };
      }

      return await axiosAssistant.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      lastError = error;
      console.warn("[AgentIA] voice endpoint failed:", endpoint, error?.response?.status || error?.message);
    }
  }

  throw lastError;
};

export default function AgentIA({
  role = "patient",
  locale = "fr",
  onBack,
  onLocaleChange,
  onOpenAssistantIA,
  context = {},
  guidedSessionKey,
  showSectionHeader = true,
}) {
  const { t } = useTranslation("assistantAi");
  const normalizedLocale = normalizeLocale(locale);
  const copy = useMemo(() => getAgentIACopy(t, normalizedLocale), [t, normalizedLocale]);
  const effectiveRole = useMemo(() => {
    const roleHint = String(role || context?.role || context?.user?.role || "").toLowerCase();
    const routeHint = String(context?.currentRoute || window.location?.pathname || "").toLowerCase();
    if (roleHint.includes("doctor") || roleHint.includes("medecin") || roleHint.includes("médecin")) return "doctor";
    if (roleHint.includes("patient")) return "patient";
    if (routeHint.includes("doctor") || routeHint.includes("medecin") || routeHint.includes("médecin")) return "doctor";
    return "patient";
  }, [role, context]);
  const inputPlaceholder = effectiveRole === "doctor" ? copy.placeholderDoctor || copy.placeholder : copy.placeholder;
  const isRtl = normalizedLocale === "ar";
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const doctorId = String(context?.userId ?? context?.user?.id ?? "").trim();
  const eventScope = `${effectiveRole}:${doctorId}:${context?.session_id ?? context?.sessionId ?? ""}`;
  const activeEventScopeRef = useRef(eventScope);
  const eventDeleteBusyRef = useRef(false);
  const eventDeleteAbortRef = useRef(null);
  useLayoutEffect(() => {
    if (activeEventScopeRef.current !== eventScope) {
      activeEventScopeRef.current = eventScope;
      eventDeleteAbortRef.current?.abort();
      setMessages([]);
    }
  }, [eventScope]);
  const [guidedBusy, setGuidedBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voicePaused, setVoicePaused] = useState(false);
  const [voicePhase, setVoicePhase] = useState("idle");
  const [voiceError, setVoiceError] = useState("");
  const [voiceGender, setVoiceGender] = useState("female");
  const inputRef = useRef(null);
  const transcriptRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const playbackRef = useRef(null);
  const discardVoiceRef = useRef(false);
  const restartVoiceAfterStopRef = useRef(false);
  const initialMicrophonePromptedRef = useRef(false);
  const speechRecognitionRef = useRef(null);
  const browserVoiceTranscriptRef = useRef("");
  const browserVoiceFinalTranscriptRef = useRef("");
  const mountedRef = useRef(true);

  const welcome = useMemo(
    () => (effectiveRole === "doctor" ? copy.welcomeDoctor : copy.welcomePatient),
    [effectiveRole, copy]
  );
  const guideSections = useMemo(() => {
    const sections = copy.guide?.[effectiveRole];
    return Array.isArray(sections) ? sections : [];
  }, [copy, effectiveRole]);
  const [openGuideSections, setOpenGuideSections] = useState({});

  useEffect(() => {
    const firstSectionId = guideSections[0]?.id;
    setOpenGuideSections(firstSectionId ? { [firstSectionId]: true } : {});
  }, [effectiveRole, normalizedLocale]);

  const toggleGuideSection = (sectionId) => {
    setOpenGuideSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  useEffect(() => {
    const element = transcriptRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
  }, [messages, loading]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      eventDeleteAbortRef.current?.abort();
      clearTimeout(recordingTimerRef.current);
      stopBrowserSpeechRecognition();
      stopMediaStream(mediaStreamRef.current);
      playbackRef.current?.pause?.();
    };
  }, []);

  const resetBrowserVoiceTranscript = () => {
    browserVoiceTranscriptRef.current = "";
    browserVoiceFinalTranscriptRef.current = "";
  };

  const stopBrowserSpeechRecognition = () => {
    const recognition = speechRecognitionRef.current;
    if (!recognition) return;

    try {
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      recognition.stop();
    } catch (error) {
      console.warn("[AgentIA] speech recognition stop failed:", error);
    } finally {
      speechRecognitionRef.current = null;
    }
  };

  const startBrowserSpeechRecognition = () => {
    const SpeechRecognition = getSpeechRecognitionConstructor();
    if (!SpeechRecognition) return;

    stopBrowserSpeechRecognition();
    resetBrowserVoiceTranscript();

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = browserSpeechLanguage(normalizedLocale);
      recognition.continuous = true;
      recognition.interimResults = true;

      recognition.onresult = (event) => {
        let finalTranscript = browserVoiceFinalTranscriptRef.current;
        let interimTranscript = "";

        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const phrase = String(event.results[index]?.[0]?.transcript || "").trim();
          if (!phrase) continue;
          if (event.results[index].isFinal) {
            finalTranscript = `${finalTranscript} ${phrase}`.trim();
          } else {
            interimTranscript = `${interimTranscript} ${phrase}`.trim();
          }
        }

        browserVoiceFinalTranscriptRef.current = finalTranscript;
        browserVoiceTranscriptRef.current = `${finalTranscript} ${interimTranscript}`.trim();
      };

      recognition.onerror = (error) => {
        console.warn("[AgentIA] speech recognition unavailable:", error?.error || error);
      };

      recognition.onend = () => {
        speechRecognitionRef.current = null;
      };

      recognition.start();
      speechRecognitionRef.current = recognition;
    } catch (error) {
      console.warn("[AgentIA] speech recognition start failed:", error);
    }
  };

  useEffect(() => {
    if (initialMicrophonePromptedRef.current) return;
    initialMicrophonePromptedRef.current = true;

    const requestInitialMicrophonePermission = async () => {
      if (isInsecureBrowserContext()) {
        setVoiceError(copy.voiceSecureContext || copy.voicePermission);
        return;
      }

      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices?.getUserMedia ||
        typeof MediaRecorder === "undefined"
      ) {
        setVoiceError(copy.voiceUnsupported);
        return;
      }

      try {
        const permissionStatus = await navigator.permissions?.query?.({ name: "microphone" });
        if (permissionStatus?.state === "granted") {
          setVoiceError("");
          return;
        }
        if (permissionStatus?.state === "denied") {
          setVoiceError(copy.voicePermission);
        }
      } catch (error) {
        console.warn("[AgentIA] microphone permission query unavailable:", error);
      }
    };

    requestInitialMicrophonePermission();

    return undefined;
  }, [copy.voicePermission, copy.voiceSecureContext, copy.voiceUnsupported]);

  const buildAssistantContext = () => ({
    ...context,
    role: effectiveRole,
    mode: "agent",
    agentMode: true,
    locale: normalizedLocale,
    response_locale: normalizedLocale,
    assistant_language: normalizedLocale,
    selected_language: normalizedLocale,
    locale_source: "user_selection",
    language_source: "user_selection",
    voice_gender: voiceGender,
    voiceGender,
  });

  const appendAgentResponse = (payload, audioBase64 = "", mimeType = "audio/mpeg") => {
    setMessages((prev) => [
      ...prev,
      {
        sender: "agent",
        text: payload?.message || "",
        links: Array.isArray(payload?.links) ? payload.links : [],
        data: payload?.data && typeof payload.data === "object" ? payload.data : {},
        meta: payload?.meta && typeof payload.meta === "object" ? payload.meta : {},
        action: payload?.action || null,
        reason: payload?.reason || "",
        toolName: payload?.tool_name || payload?.toolName || "",
        actionName: payload?.action_name || payload?.actionName || "",
        ok: payload?.ok,
        audioBase64,
        mimeType,
      },
    ]);
  };

  const deleteEventFromCard = async (eventId, messageIndex, sourceData) => {
    const scope = eventScope;
    if (effectiveRole !== "doctor" || !doctorId || eventDeleteBusyRef.current || !Number.isSafeInteger(eventId)) {
      throw new Error("EVENT_DELETE_UNAVAILABLE");
    }
    eventDeleteBusyRef.current = true;
    const controller = new AbortController();
    eventDeleteAbortRef.current = controller;
    let deleteAccepted = false;
    try {
      await axiosEvents.delete(ENDPOINTS.EVENTS.DELETE(eventId), {
        withCredentials: true,
        signal: controller.signal,
      });
      deleteAccepted = true;
      if (!mountedRef.current || activeEventScopeRef.current !== scope) return;
      const response = await axiosEvents.get(ENDPOINTS.EVENTS.BY_DOCTOR(doctorId), {
        withCredentials: true,
        signal: controller.signal,
      });
      const freshEvents = Array.isArray(response?.data) ? response.data : response?.data?.events;
      if (!Array.isArray(freshEvents) || freshEvents.some((event) => structuredEventId(event) === eventId)) {
        throw new Error("EVENTS_REFRESH_FAILED");
      }
      if (!mountedRef.current || activeEventScopeRef.current !== scope) return;
      const filteredEvents = eventsInOriginalRange(freshEvents, sourceData);
      setMessages((previous) => previous.map((message, index) => index === messageIndex ? {
        ...message,
        text: eventCancelText(normalizedLocale).list(filteredEvents.length),
        data: { ...message.data, events: filteredEvents, count: filteredEvents.length },
      } : message));
    } catch (error) {
      if (deleteAccepted && error?.message !== "EVENTS_REFRESH_FAILED") {
        throw new Error("EVENTS_REFRESH_FAILED");
      }
      throw error;
    } finally {
      eventDeleteBusyRef.current = false;
      if (eventDeleteAbortRef.current === controller) eventDeleteAbortRef.current = null;
    }
  };

  const appendGuidedResponse = (reply) => {
    setMessages((prev) => [
      ...prev,
      {
        sender: "agent",
        text: reply?.message || "",
        links: [],
        data: {},
        meta: reply?.meta && typeof reply.meta === "object" ? reply.meta : {},
        action: null,
        ui: reply?.ui || null,
      },
    ]);
  };

  const playAssistantAudio = async (audioBase64, mimeType = "audio/mpeg") => {
    if (!audioBase64) return;
    try {
      stopAssistantAudio(false);
      const audio = new Audio(`data:${mimeType};base64,${audioBase64}`);
      playbackRef.current = audio;
      setVoicePhase("speaking");
      audio.onended = () => {
        if (playbackRef.current === audio) {
          playbackRef.current = null;
          setVoicePhase("idle");
        }
      };
      audio.onerror = () => {
        if (playbackRef.current === audio) {
          playbackRef.current = null;
          setVoicePhase("idle");
        }
      };
      await audio.play();
    } catch (error) {
      console.warn("[AgentIA] audio playback failed:", error);
      playbackRef.current = null;
      setVoicePhase("idle");
    }
  };

  const stopAssistantAudio = (resetPhase = true) => {
    const audio = playbackRef.current;
    if (audio) {
      audio.pause?.();
      try {
        audio.currentTime = 0;
      } catch (error) {
        console.warn("[AgentIA] audio reset failed:", error);
      }
    }
    playbackRef.current = null;
    if (resetPhase) {
      setVoicePhase("idle");
    }
  };

  const submitMessage = async (rawText, visibleText = rawText) => {
    const text = String(rawText || "").trim();
    if (!text || loading || guidedBusy) return;

    setLoading(true);
    setMessages((prev) => [...prev, { sender: "user", text: visibleText || text }]);

    try {
      const { data } = await axiosAssistant.post(ENDPOINTS.ASSISTANT.CHAT, {
        message: text,
        context: buildAssistantContext(),
      });

      if (!mountedRef.current) return;
      appendAgentResponse(data);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("[AgentIA] request failed:", error);
      setMessages((prev) => [...prev, { sender: "agent", text: copy.error, data: {} }]);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        inputRef.current?.focus();
      }
    }
  };

  const guidedSessionId = () => {
    if (guidedSessionKey) return guidedSessionKey;
    const ctx = buildAssistantContext();
    return ctx.session_id || ctx.sessionId || context?.session_id || context?.sessionId || null;
  };

  const startBookingFromDoctorCard = async (doctor, visibleText) => {
    const doctorId = doctor?.id || doctor?.doctorId || doctor?.medecinId || doctor?.raw?.id;
    if (!doctorId) {
      await submitMessage(`prendre rendez-vous avec ${doctor?.name || doctor?.fullName || ""}`.trim(), visibleText);
      return;
    }

    setGuidedBusy(true);
    setMessages((prev) => [...prev, { sender: "user", text: visibleText || copy.cards?.book || "Prendre rendez-vous" }]);

    try {
      const sessionId = guidedSessionId();
      await startGuidedFlow({
        sessionId,
        role: "patient",
        actionId: PATIENT_BOOK_APPOINTMENT,
        locale: normalizedLocale,
        context: buildAssistantContext(),
      });

      const reply = await continueGuidedFlow({
        sessionId,
        role: "patient",
        flowId: PATIENT_BOOK_APPOINTMENT,
        stepId: "choose_doctor",
        locale: normalizedLocale,
        payload: {
          doctorId: String(doctorId),
          doctor: { ...doctor, id: String(doctorId), registered: true },
        },
      });
      appendGuidedResponse(reply);
    } catch (error) {
      console.error("[AgentIA] guided booking failed:", error);
      setMessages((prev) => [...prev, { sender: "agent", text: copy.error, data: {} }]);
    } finally {
      setGuidedBusy(false);
      inputRef.current?.focus();
    }
  };

  const startAppointmentManagementFromCard = async (appointment, actionId, visibleText) => {
    const appointmentId = appointment?.id || appointment?.appointmentId;
    if (!appointmentId || effectiveRole !== "patient") {
      const command = actionId === PATIENT_RESCHEDULE_APPOINTMENT
        ? `modifier rendez-vous #${appointmentId || ""}`.trim()
        : `annuler rendez-vous #${appointmentId || ""}`.trim();
      await submitMessage(command, visibleText);
      return;
    }

    setGuidedBusy(true);
    setMessages((prev) => [...prev, { sender: "user", text: visibleText || String(appointmentId) }]);

    try {
      const sessionId = guidedSessionId();
      await startGuidedFlow({
        sessionId,
        role: "patient",
        actionId,
        locale: normalizedLocale,
        context: buildAssistantContext(),
      });

      const reply = await continueGuidedFlow({
        sessionId,
        role: "patient",
        flowId: actionId,
        stepId: "choose_appointment",
        locale: normalizedLocale,
        payload: {
          appointmentId: String(appointmentId),
          appointment,
        },
      });
      appendGuidedResponse(reply);
    } catch (error) {
      console.error("[AgentIA] guided appointment management failed:", error);
      setMessages((prev) => [...prev, { sender: "agent", text: copy.error, data: {} }]);
    } finally {
      setGuidedBusy(false);
      inputRef.current?.focus();
    }
  };

  const continueGuidedMessage = async (props, payload = {}) => {
    if (!props?.flowId || guidedBusy) return;
    setGuidedBusy(true);
    try {
      const reply = await continueGuidedFlow({
        sessionId: guidedSessionId(),
        role: effectiveRole,
        flowId: props.flowId,
        stepId: props.stepId,
        locale: normalizedLocale,
        payload,
      });
      appendGuidedResponse(reply);
    } catch (error) {
      console.error("[AgentIA] guided continue failed:", error);
      setMessages((prev) => [...prev, { sender: "agent", text: copy.error, data: {} }]);
    } finally {
      setGuidedBusy(false);
    }
  };

  const cancelGuidedMessage = async () => {
    if (guidedBusy) return;
    setGuidedBusy(true);
    try {
      const reply = await cancelGuidedFlow({
        sessionId: guidedSessionId(),
        role: effectiveRole,
      });
      appendGuidedResponse(reply);
    } catch (error) {
      console.error("[AgentIA] guided cancel failed:", error);
    } finally {
      setGuidedBusy(false);
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const text = input.trim();
    setInput("");
    await submitMessage(text);
  };

  const submitVoiceBlob = async (blob, browserTranscript = "") => {
    if (!blob || blob.size <= 0) {
      setVoiceError(copy.voiceError);
      setVoicePhase("idle");
      return;
    }

    setLoading(true);
    setVoicePhase("processing");
    setVoiceError("");

    try {
      const formData = new FormData();
      const mimeType = blob.type || "audio/webm";
      formData.append("file", blob, `agent-voice-${Date.now()}.${extensionFromMime(mimeType)}`);
      formData.append("language", normalizedLocale === "ar" ? "ar" : normalizedLocale === "en" ? "en" : "fr");
      formData.append("voice_gender", voiceGender);
      formData.append("context", JSON.stringify(buildAssistantContext()));
      const cleanBrowserTranscript = String(browserTranscript || "").trim();
      if (cleanBrowserTranscript) {
        formData.append("transcript", cleanBrowserTranscript);
      }

      const { data } = await postVoiceChat(formData);
      if (!mountedRef.current) return;

      const transcript = String(data?.transcript || "").trim() || copy.voiceTranscriptFallback;
      const responsePayload = data?.response && typeof data.response === "object" ? data.response : {};
      const audioBase64 = data?.audio_base64 || "";
      const responseMimeType = data?.mime_type || "audio/mpeg";

      setMessages((prev) => [...prev, { sender: "user", text: transcript, viaVoice: true }]);
      appendAgentResponse(responsePayload, audioBase64, responseMimeType);
      await playAssistantAudio(audioBase64, responseMimeType);
    } catch (error) {
      if (!mountedRef.current) return;
      console.error("[AgentIA] voice request failed:", error);
      setVoiceError(copy.voiceError);
      setMessages((prev) => [...prev, { sender: "agent", text: copy.voiceError, data: {} }]);
      setVoicePhase("idle");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        inputRef.current?.focus();
      }
    }
  };

  const startVoiceRecording = async () => {
    if (loading || guidedBusy || recording) return;
    if (isInsecureBrowserContext()) {
      setVoiceError(copy.voiceSecureContext || copy.voicePermission);
      setVoicePhase("idle");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceError(copy.voiceUnsupported);
      return;
    }

    try {
      setVoiceError("");
      setVoicePaused(false);
      discardVoiceRef.current = false;
      restartVoiceAfterStopRef.current = false;
      stopAssistantAudio(false);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: MICROPHONE_AUDIO_CONSTRAINTS,
      });
      const mimeType = preferredRecordingMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];
      resetBrowserVoiceTranscript();

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        clearTimeout(recordingTimerRef.current);
        const browserTranscript = browserVoiceTranscriptRef.current || browserVoiceFinalTranscriptRef.current;
        stopBrowserSpeechRecognition();
        const shouldDiscard = discardVoiceRef.current;
        const shouldRestart = restartVoiceAfterStopRef.current;
        discardVoiceRef.current = false;
        restartVoiceAfterStopRef.current = false;
        setRecording(false);
        setVoicePaused(false);
        stopMediaStream(stream);
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        const finalType = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(audioChunksRef.current, { type: finalType });
        audioChunksRef.current = [];
        if (shouldDiscard) {
          setVoicePhase("idle");
          if (shouldRestart) {
            window.setTimeout(() => startVoiceRecording(), 120);
          }
          return;
        }
        submitVoiceBlob(blob, browserTranscript);
      };

      recorder.start();
      startBrowserSpeechRecognition();
      setRecording(true);
      setVoicePhase("recording");
      recordingTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_MS);
    } catch (error) {
      console.error("[AgentIA] microphone unavailable:", error);
      stopBrowserSpeechRecognition();
      setVoiceError(copy.voicePermission);
      setVoicePhase("idle");
      setRecording(false);
      setVoicePaused(false);
      stopMediaStream(mediaStreamRef.current);
      mediaStreamRef.current = null;
    }
  };

  const stopVoiceRecording = ({ submit = true, restart = false } = {}) => {
    clearTimeout(recordingTimerRef.current);
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      discardVoiceRef.current = !submit;
      restartVoiceAfterStopRef.current = restart;
      setVoicePhase(submit ? "processing" : "idle");
      recorder.stop();
      return;
    }
    setRecording(false);
    setVoicePaused(false);
    setVoicePhase("idle");
    stopBrowserSpeechRecognition();
    stopMediaStream(mediaStreamRef.current);
    mediaStreamRef.current = null;
  };

  const pauseVoiceRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || !recording) return;
    if (recorder.state === "recording" && typeof recorder.pause === "function") {
      recorder.pause();
      setVoicePaused(true);
      setVoicePhase("paused");
      return;
    }
    if (recorder.state === "paused" && typeof recorder.resume === "function") {
      recorder.resume();
      setVoicePaused(false);
      setVoicePhase("recording");
    }
  };

  const retryVoiceRecording = () => {
    setVoiceError("");
    if (recording || mediaRecorderRef.current) {
      stopVoiceRecording({ submit: false, restart: true });
      return;
    }
    setVoicePhase("idle");
    startVoiceRecording();
  };

  const handleVoiceToggle = () => {
    if (recording) {
      stopVoiceRecording();
      return;
    }
    startVoiceRecording();
  };

  const handleOpenUrl = (url, external = false) => {
    if (!url) return;
    if (external || /^https?:\/\//i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    window.location.assign(url);
  };

  return (
    <div className={`agent-ia-page ${showSectionHeader ? "" : "agent-ia-page--embedded"}`} dir={isRtl ? "rtl" : "ltr"}>
      {showSectionHeader && (
        <SectionHeader
          section="agent"
          locale={normalizedLocale}
          onLocaleChange={onLocaleChange}
          onBack={onBack}
        />
      )}

      <div className="agent-ia-content">
        <aside className="agent-ia-history agent-ia-guide">
          <span className="agent-ia-kicker">{copy.guideTitle || copy.recentTitle}</span>
          {copy.guideSubtitle && <p className="agent-guide-subtitle">{copy.guideSubtitle}</p>}
          <div className="agent-guide-sections">
            {guideSections.map((section) => {
              const Icon = GUIDE_ICONS[section.id] || ClipboardList;
              const actions = Array.isArray(section.actions) ? section.actions : [];
              const isOpen = Boolean(openGuideSections[section.id]);

              return (
                <section className="agent-guide-section" key={section.id}>
                  <button
                    type="button"
                    className="agent-guide-section-toggle"
                    onClick={() => toggleGuideSection(section.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="agent-guide-icon" aria-hidden="true">
                      <Icon size={15} strokeWidth={2.4} />
                    </span>
                    <span className="agent-guide-title">
                      <strong>{section.title}</strong>
                      <small>{formatGuideCount(copy, actions.length)}</small>
                    </span>
                    <span className="agent-guide-count" aria-hidden="true">{actions.length}</span>
                    <ChevronDown className={isOpen ? "is-open" : ""} size={15} strokeWidth={2.4} aria-hidden="true" />
                  </button>

                  {isOpen && (
                    <div className="agent-guide-actions">
                      {actions.map((action) => (
                        <article className="agent-guide-action" key={`${section.id}-${action.title}`}>
                          <strong>{action.title}</strong>
                          <p>{action.description}</p>
                          <small>
                            <span>{copy.guideExampleLabel || "Ex."}</span>
                            {action.example}
                          </small>
                        </article>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </aside>

        <main className="agent-ia-main">
          <section className="agent-ia-hero">
            <span className="agent-ia-orb" aria-hidden="true">IA</span>
            <div>
              <h2>{copy.title}</h2>
              <p>{copy.subtitle}</p>
            </div>
          </section>

          <div className="agent-ia-transcript" ref={transcriptRef}>
            <div className="agent-ia-message agent">
              <strong>{copy.agent}</strong>
              <p>{welcome}</p>
            </div>

            {messages.map((message, index) => {
              const isStructuredAgentMessage = message.sender === "agent" && hasStructuredData(message?.data);
              return (
              <div className={`agent-ia-message ${message.sender} ${isStructuredAgentMessage ? "agent-ia-message--structured" : ""}`} key={`${message.sender}-${index}`}>
                <strong>{message.sender === "user" ? copy.you : copy.agent}</strong>
                {message.sender === "agent" ? (
                  message.ui ? (
                    <>
                      {message.text && <p>{message.text}</p>}
                      <GuidedFlowRenderer
                        ui={message.ui}
                        onContinue={continueGuidedMessage}
                        onCancel={cancelGuidedMessage}
                        onShowMenu={() => {}}
                      />
                    </>
                  ) : (
                    <AgentStructuredResponse
                      message={message}
                      copy={copy}
                      locale={normalizedLocale}
                      role={effectiveRole}
                      onQuickCommand={submitMessage}
                      onBookDoctor={startBookingFromDoctorCard}
                      onManageAppointment={startAppointmentManagementFromCard}
                      onOpenUrl={handleOpenUrl}
                      onDeleteEvent={(eventId, sourceData) => deleteEventFromCard(eventId, index, sourceData)}
                      eventScope={eventScope}
                      canDeleteEvents={effectiveRole === "doctor" && Boolean(doctorId)}
                    />
                  )
                ) : (
                  <p>{message.text}</p>
                )}
                {message.sender === "agent" && isMedicalAssistantRedirectMessage(message) && (
                  <button
                    type="button"
                    className="agent-medical-assistant-btn"
                    onClick={() => onOpenAssistantIA?.()}
                    disabled={typeof onOpenAssistantIA !== "function"}
                    title={getMedicalAssistantRedirectLabel(message, normalizedLocale)}
                  >
                    <HeartPulse size={15} strokeWidth={2.4} />
                    <span>{getMedicalAssistantRedirectLabel(message, normalizedLocale)}</span>
                  </button>
                )}
                {message.sender === "agent" && message.links?.length > 0 && (
                  <div className="agent-ia-links">
                    {message.links.map((link, linkIndex) => (
                      <button
                        type="button"
                        key={`${link.url}-${linkIndex}`}
                        onClick={() => handleOpenUrl(link.url, link.external)}
                      >
                        {displayLinkLabel(link.text, copy)}
                      </button>
                    ))}
                  </div>
                )}
                {message.sender === "agent" && message.audioBase64 && (
                  <button
                    type="button"
                    className="agent-audio-replay"
                    onClick={() => playAssistantAudio(message.audioBase64, message.mimeType)}
                  >
                    <Volume2 size={15} strokeWidth={2.4} />
                    <span>{copy.voiceReplay}</span>
                  </button>
                )}
              </div>
              );
            })}

            {(loading || guidedBusy) && (
              <div className="agent-ia-message agent agent-ia-message--loading" aria-live="polite">
                <strong>{copy.agent}</strong>
                <div className="agent-ia-loader-card">
                  <span className="agent-ia-loader-icon" aria-hidden="true" />
                  <div className="agent-ia-loader-copy">
                    <span>{copy.thinking}</span>
                    <small>MediLink</small>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form className="agent-input-bar" onSubmit={handleSubmit}>
            <VoiceStatusDock
              copy={copy}
              phase={voicePhase}
              error={voiceError}
              recording={recording}
              paused={voicePaused}
              onPauseToggle={pauseVoiceRecording}
              onRetry={retryVoiceRecording}
              onStopPlayback={stopAssistantAudio}
            />
            <div className="agent-input-toolbar">
              <div className="agent-voice-preference" role="group" aria-label={copy.voiceGenderLabel}>
                <span>{copy.voiceGenderLabel}</span>
                <button
                  type="button"
                  className={voiceGender === "female" ? "is-active" : ""}
                  onClick={() => setVoiceGender("female")}
                  disabled={loading || guidedBusy || recording}
                  aria-pressed={voiceGender === "female"}
                >
                  <Venus size={14} strokeWidth={2.4} />
                  {copy.voiceFemale}
                </button>
                <button
                  type="button"
                  className={voiceGender === "male" ? "is-active" : ""}
                  onClick={() => setVoiceGender("male")}
                  disabled={loading || guidedBusy || recording}
                  aria-pressed={voiceGender === "male"}
                >
                  <Mars size={14} strokeWidth={2.4} />
                  {copy.voiceMale}
                </button>
              </div>
            </div>
            <div className="agent-input-row">
              <button
                type="button"
                className={`agent-voice-btn ${recording ? "is-recording" : ""}`}
                onClick={handleVoiceToggle}
                disabled={(loading || guidedBusy) && !recording}
                aria-label={recording ? copy.voiceStop : copy.voiceStart}
                title={recording ? copy.voiceStop : copy.voiceStart}
              >
                {recording ? <Square size={17} strokeWidth={2.6} /> : <Mic size={18} strokeWidth={2.6} />}
                <span>{recording ? copy.voiceStop : copy.voiceStart}</span>
              </button>
              <input
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder={inputPlaceholder}
                disabled={loading || guidedBusy || recording}
              />
              <button type="submit" className="agent-send-btn" disabled={loading || guidedBusy || recording || !input.trim()}>
                {copy.send}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}

function VoiceStatusDock({
  copy,
  phase,
  error,
  recording,
  paused,
  onPauseToggle,
  onRetry,
  onStopPlayback,
}) {
  const active = phase !== "idle" || Boolean(error);
  if (!active) return null;

  const label = error
    || (phase === "recording"
      ? copy.voiceRecording
      : phase === "speaking"
        ? copy.voiceSpeaking
        : phase === "paused"
          ? copy.voicePaused
          : copy.voiceProcessing);

  return (
    <div
      className={`agent-voice-dock ${recording ? "is-recording" : ""} ${paused ? "is-paused" : ""} ${error ? "has-error" : ""}`}
    >
      <div className="agent-voice-orb" aria-hidden="true">
        <span />
        <i />
        <b />
      </div>
      <div className="agent-voice-copy">
        <strong>{label}</strong>
        <small>{copy.voiceHint}</small>
      </div>
      <div className="agent-voice-wave" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <div className="agent-voice-inline-actions">
        {recording && (
          <button type="button" onClick={onPauseToggle}>
            {paused ? <Play size={14} strokeWidth={2.4} /> : <Pause size={14} strokeWidth={2.4} />}
            <span>{paused ? copy.voiceResume : copy.voicePause}</span>
          </button>
        )}
        {(recording || error) && (
          <button type="button" onClick={onRetry}>
            <RotateCcw size={14} strokeWidth={2.4} />
            <span>{copy.voiceRetry}</span>
          </button>
        )}
        {phase === "speaking" && (
          <button type="button" className="is-danger" onClick={() => onStopPlayback?.()}>
            <Square size={14} strokeWidth={2.4} />
            <span>{copy.voiceStopPlayback}</span>
          </button>
        )}
      </div>
    </div>
  );
}

function AgentStructuredResponse({ message, copy, locale, role, onQuickCommand, onBookDoctor, onManageAppointment, onOpenUrl, onDeleteEvent, eventScope, canDeleteEvents }) {
  const data = message?.data || {};

  if (!hasStructuredData(data)) {
    return <p>{message.text}</p>;
  }

  const intro = buildStructuredIntro({
    data,
    text: message?.text,
    copy,
    locale,
    role,
  });

  return (
    <div className="agent-response-stack">
      {intro && <div className="agent-response-intro">{intro}</div>}
      <DoctorResultCards data={data} copy={copy} locale={locale} onQuickCommand={onQuickCommand} onBookDoctor={onBookDoctor} onOpenUrl={onOpenUrl} />
      <PatientResultCards data={data} copy={copy} locale={locale} onQuickCommand={onQuickCommand} onOpenUrl={onOpenUrl} />
      <PlaceResultCards data={data} copy={copy} locale={locale} onOpenUrl={onOpenUrl} />
      <SlotResultCards data={data} copy={copy} locale={locale} onQuickCommand={onQuickCommand} />
      <BookingConfirmationCard data={data} copy={copy} locale={locale} onQuickCommand={onQuickCommand} />
      <AppointmentResultCards
        data={data}
        copy={copy}
        locale={locale}
        role={role}
        onQuickCommand={onQuickCommand}
        onManageAppointment={onManageAppointment}
      />
      <AvailabilityResultCards data={data} copy={copy} locale={locale} onQuickCommand={onQuickCommand} />
      <EventResultCards data={data} copy={copy} locale={locale} canDeleteEvents={canDeleteEvents} eventScope={eventScope} onOpenUrl={onOpenUrl} onDeleteEvent={onDeleteEvent} />
      <MedicationResultCards data={data} copy={copy} locale={locale} onOpenUrl={onOpenUrl} />
      <MedicalRecordResultCards data={data} copy={copy} locale={locale} onOpenUrl={onOpenUrl} />
      <ConfirmationResultCard data={data} copy={copy} />
    </div>
  );
}

function hasStructuredData(data) {
  if (!data || typeof data !== "object") return false;
  return [
    "doctors",
    "patients",
    "patient_choices",
    "places",
    "appointment_slots",
    "available_days",
    "available_periods",
    "appointments",
    "slots",
    "events",
    "event",
    "medications",
    "active_medications",
    "inactive_medications",
    "medical_info",
    "medical_record",
    "patient",
    "document_counts",
    "bmi",
    "bmi_history",
    "allergies",
    "vaccines",
    "appointment",
    "selected_slot",
    "requires_confirmation",
    "created",
    "updated",
    "deleted",
  ].some((key) => data[key] != null);
}

function buildStructuredIntro({ data, text, copy, locale }) {
  const clean = sanitizeStructuredText(text);
  if (data?.medical_record && clean) return clean;
  if (clean && !isNoisyStructuredText(text) && clean.length <= 220) {
    return clean;
  }

  const summary = structuredSummary(data, copy, locale);
  return summary || clean;
}

function structuredSummary(data, copy, locale) {
  const doctors = countArray(data?.doctors);
  const patients = countArray(data?.patients);
  const patientChoices = countArray(data?.patient_choices);
  const places = countArray(data?.places);
  const appointmentSlots = countArray(data?.appointment_slots);
  const days = countArray(data?.available_days);
  const periods = countArray(data?.available_periods);
  const appointments = Array.isArray(data?.appointments) ? data.appointments.length : 0;
  const availabilitySlots = countArray(data?.slots);
  const events = Array.isArray(data?.events) ? data.events.length : data?.event ? 1 : 0;
  const medications = countArray(data?.medications) + countArray(data?.active_medications) + countArray(data?.inactive_medications);
  const recordFound = Boolean(data?.medical_info || data?.medical_record || data?.patient || data?.document_counts || data?.bmi);

  if (data?.requires_confirmation && data?.selected_slot) {
    return summarySentence(locale, {
      fr: "Derniere etape : confirmez le creneau selectionne pour creer la demande.",
      en: "Final step: confirm the selected slot to create the appointment request.",
      ar: "الخطوة الأخيرة: أكد الموعد المختار لإنشاء الطلب.",
    });
  }
  if (appointments) {
    return summarySentence(locale, {
      fr: `${appointments} rendez-vous trouve${appointments > 1 ? "s" : ""}. Les cartes ci-dessous affichent les informations utiles et les actions disponibles.`,
      en: `${appointments} appointment${appointments > 1 ? "s" : ""} found. The cards below show the useful details and available actions.`,
      ar: `تم العثور على ${appointments} موعد. تعرض البطاقات أدناه التفاصيل والإجراءات المتاحة.`,
    });
  }
  if (appointmentSlots) {
    return summarySentence(locale, {
      fr: `${appointmentSlots} creneau${appointmentSlots > 1 ? "x" : ""} disponible${appointmentSlots > 1 ? "s" : ""}. Choisissez un creneau depuis les boutons ci-dessous.`,
      en: `${appointmentSlots} available slot${appointmentSlots > 1 ? "s" : ""}. Choose one from the buttons below.`,
      ar: `${appointmentSlots} موعد متاح. اختر موعداً من الأزرار أدناه.`,
    });
  }
  if (doctors) {
    return summarySentence(locale, {
      fr: `${doctors} medecin${doctors > 1 ? "s" : ""} trouve${doctors > 1 ? "s" : ""}. Les cartes indiquent les profils MediLink et les actions possibles.`,
      en: `${doctors} doctor${doctors > 1 ? "s" : ""} found. The cards show MediLink profiles and possible actions.`,
      ar: `تم العثور على ${doctors} طبيب. تعرض البطاقات ملفات MediLink والإجراءات الممكنة.`,
    });
  }
  if (patients) {
    return summarySentence(locale, {
      fr: `${patients} patient${patients > 1 ? "s" : ""} autorise${patients > 1 ? "s" : ""}. Ouvrez le dossier souhaite depuis les cartes ci-dessous.`,
      en: `${patients} authorized patient${patients > 1 ? "s" : ""}. Open the record you need from the cards below.`,
      ar: `${patients} مريض مصرح به. افتح الملف المطلوب من البطاقات أدناه.`,
    });
  }
  if (patientChoices) {
    return summarySentence(locale, {
      fr: `${patientChoices} patient${patientChoices > 1 ? "s" : ""} proche${patientChoices > 1 ? "s" : ""} trouve${patientChoices > 1 ? "s" : ""}. Choisissez le bon dossier.`,
      en: `${patientChoices} close patient match${patientChoices > 1 ? "es" : ""} found. Choose the right record.`,
      ar: `تم العثور على ${patientChoices} مريض قريب من الاسم. اختر الملف الصحيح.`,
    });
  }
  if (places) {
    return summarySentence(locale, {
      fr: `${places} lieu${places > 1 ? "x" : ""} medical${places > 1 ? "aux" : ""} trouve${places > 1 ? "s" : ""}. Utilisez les cartes pour ouvrir la page ou Google Maps.`,
      en: `${places} medical location${places > 1 ? "s" : ""} found. Use the cards to open the page or Google Maps.`,
      ar: `تم العثور على ${places} مكان طبي. استخدم البطاقات لفتح الصفحة أو Google Maps.`,
    });
  }
  if (days || periods) {
    return summarySentence(locale, {
      fr: `${days + periods} option${days + periods > 1 ? "s" : ""} de disponibilite trouvee${days + periods > 1 ? "s" : ""}. Selectionnez une date ou une periode pour afficher les creneaux.`,
      en: `${days + periods} availability option${days + periods > 1 ? "s" : ""} found. Select a date or period to view slots.`,
      ar: `تم العثور على ${days + periods} خيار توفر. اختر تاريخاً أو فترة لعرض المواعيد.`,
    });
  }
  if (availabilitySlots) {
    return summarySentence(locale, {
      fr: `${availabilitySlots} disponibilite${availabilitySlots > 1 ? "s" : ""} chargee${availabilitySlots > 1 ? "s" : ""}. Les cartes distinguent les creneaux disponibles et indisponibles.`,
      en: `${availabilitySlots} availability slot${availabilitySlots > 1 ? "s" : ""} loaded. The cards separate available and unavailable slots.`,
      ar: `تم تحميل ${availabilitySlots} توفر. تميز البطاقات بين المواعيد المتاحة وغير المتاحة.`,
    });
  }
  if (events) {
    return summarySentence(locale, {
      fr: `${events} evenement${events > 1 ? "s" : ""} trouve${events > 1 ? "s" : ""}.`,
      en: `${events} event${events > 1 ? "s" : ""} found.`,
      ar: `تم العثور على ${events} حدث.`,
    });
  }
  if (medications) {
    return summarySentence(locale, {
      fr: `${medications} medicament${medications > 1 ? "s" : ""} trouve${medications > 1 ? "s" : ""}.`,
      en: `${medications} medication${medications > 1 ? "s" : ""} found.`,
      ar: `تم العثور على ${medications} دواء.`,
    });
  }
  if (recordFound) {
    return summarySentence(locale, {
      fr: "Dossier medical charge. Les informations principales sont organisees ci-dessous.",
      en: "Medical record loaded. Key information is organized below.",
      ar: "تم تحميل الملف الطبي. المعلومات الأساسية منظمة أدناه.",
    });
  }
  if (data?.created || data?.updated || data?.deleted || data?.payload || data?.selected_slot) {
    return summarySentence(locale, {
      fr: "Action traitee. Le resultat est affiche ci-dessous.",
      en: "Action processed. The result is shown below.",
      ar: "تم تنفيذ الإجراء. تظهر النتيجة أدناه.",
    });
  }
  return "";
}

function summarySentence(locale, values) {
  const normalized = normalizeLocale(locale);
  return values[normalized] || values.fr || "";
}

function countArray(value) {
  return Array.isArray(value) ? value.length : 0;
}

function ExpandableItems({ items, initialCount, className, renderItem, copy }) {
  const [expanded, setExpanded] = useState(false);
  const safeItems = Array.isArray(items) ? items : [];
  const visibleItems = expanded ? safeItems : safeItems.slice(0, initialCount);
  const hiddenCount = Math.max(0, safeItems.length - initialCount);
  const showMoreLabel = copy?.showMore || "Voir plus";
  const showLessLabel = copy?.showLess || "Reduire";

  return (
    <>
      <div className={className}>
        {visibleItems.map((item, index) => renderItem(item, index))}
      </div>
      {hiddenCount > 0 && (
        <button
          type="button"
          className={`agent-list-toggle ${expanded ? "is-open" : ""}`}
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? showLessLabel : `${showMoreLabel} (${hiddenCount})`}
        </button>
      )}
    </>
  );
}

function sanitizeStructuredText(text = "") {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !isStructuredEchoLine(line));
  return lines.join("\n").trim();
}

function isNoisyStructuredText(text = "") {
  const lines = String(text || "").split(/\r?\n/).filter((line) => line.trim());
  if (lines.length > 5) return true;
  return lines.some(isStructuredEchoLine);
}

function isStructuredEchoLine(line = "") {
  const value = String(line || "").trim();
  if (!value) return true;
  if (/^[-*]\s*#?\d+\s*\|/.test(value)) return true;
  if (/^#?\d+\s*\|\s*/.test(value)) return true;
  if (/^[-*]\s*#?\d+\s+/.test(value) && /\b(scheduled|confirmed|cancelled|pending|rejected|completed)\b/i.test(value)) return true;
  if (/^(vos rendez-vous|rendez-vous|appointments|creneaux disponibles|créneaux disponibles|disponibilites|disponibilités)\s*(\(\d+\))?\s*:?$/i.test(value)) return true;
  if (/^\d+\s+(rendez-vous|appointments|creneaux|créneaux|medecins|médecins|lieux)/i.test(value)) return true;
  if (/\|\s*(scheduled|confirmed|cancelled|pending|rejected|completed)\b/i.test(value)) return true;
  return false;
}

function PatientResultCards({ data, copy, locale, onQuickCommand, onOpenUrl }) {
  const patients = Array.isArray(data?.patients) ? data.patients : [];
  const choices = Array.isArray(data?.patient_choices) ? data.patient_choices : [];
  const items = patients.length ? patients : choices;
  if (!items.length) return null;

  const isChoiceList = !patients.length && choices.length > 0;
  const title = isChoiceList ? copy.cards.patientChoices : copy.cards.authorizedPatients;

  return (
    <section className="agent-data-section">
      <SectionTitle title={title || copy.cards.patientRecord} count={items.length} />
      <ExpandableItems
        items={items}
        initialCount={10}
        className="agent-card-list"
        copy={copy}
        renderItem={(patient, index) => {
          const name = personDisplayName(patient) || patient?.name || patient?.identifier || "Patient";
          const route = patient?.route || patient?.url;
          const actionLabel = patient?.actionLabel || copy.cards.openRecord || copy.cards.openPage;
          const command = patient?.command || `ouvrir le dossier de ${name}`;

          return (
            <article className="agent-result-card patient" key={`${patient?.id || name}-${index}`}>
              <div className="agent-result-card__top">
                <div>
                  <h3>{name}</h3>
                  {patient?.identifier && <p>{patient.identifier}</p>}
                </div>
                {isChoiceList && <span className="agent-pill is-muted">{copy.cards.closeMatch || "Correspondance"}</span>}
              </div>
              <div className="agent-chip-row">
                {patient?.phone && <span className="agent-chip">{copy.cards.phone}: {patient.phone}</span>}
                {patient?.gender && <span className="agent-chip">{patient.gender}</span>}
                {patient?.birthdate && <span className="agent-chip">{formatDate(patient.birthdate, locale)}</span>}
              </div>
              <div className="agent-card-actions">
                <button
                  type="button"
                  className="agent-action-primary"
                  onClick={() => route ? onOpenUrl(route) : onQuickCommand(command, actionLabel)}
                >
                  {actionLabel}
                </button>
              </div>
            </article>
          );
        }}
      />
    </section>
  );
}

function DoctorResultCards({ data, copy, locale, onQuickCommand, onBookDoctor, onOpenUrl }) {
  const doctors = Array.isArray(data?.doctors) ? data.doctors : [];
  if (!doctors.length) return null;

  return (
    <section className="agent-data-section">
      <SectionTitle title={copy.cards.doctors} count={doctors.length} />
      <ExpandableItems
        items={doctors}
        initialCount={10}
        className="agent-card-list agent-card-list--doctors"
        copy={copy}
        renderItem={(doctor, index) => {
          const name = doctor?.name || doctor?.fullName || doctor?.raw?.fullName || doctor?.raw?.name || "Médecin";
          const canBook = doctor?.canBook === true || doctor?.can_book === true || doctor?.registered === true || doctor?.isMediLink === true;
          const distance = doctor?.distanceLabel || doctor?.distanceText || doctor?.distance || (doctor?.distance_km != null ? `${formatNumber(doctor.distance_km)} km` : "");
          const rating = doctor?.rating || doctor?.raw?.rating;
          const address = doctor?.address || doctor?.clinicAddress || doctor?.city || "";
          const command = canBook
            ? `prendre rendez-vous avec ${name}`
            : `inviter ${name} à rejoindre MediLink`;

          return (
            <article className={`agent-result-card doctor ${canBook ? "is-medilink" : "is-external"}`} key={`${doctor?.id || name}-${index}`}>
              <div className="agent-result-card__top">
                <div>
                  <h3>{name}</h3>
                  <p>{doctor?.specialty || doctor?.specialite || "Spécialité non précisée"}</p>
                </div>
                <span className={`agent-pill ${canBook ? "is-primary" : "is-muted"}`}>
                  {canBook ? copy.cards.medilinkDoctor : copy.cards.externalDoctor}
                </span>
              </div>
              {address && <div className="agent-card-muted">{address}</div>}
              <div className="agent-chip-row">
                {distance && <span className="agent-chip">{copy.cards.distance}: {distance}</span>}
                {rating && <span className="agent-chip">{copy.cards.rating}: {rating}/5</span>}
                {doctor?.phone && <span className="agent-chip">{copy.cards.phone}: {doctor.phone}</span>}
              </div>
              <div className="agent-card-actions">
                <button
                  type="button"
                  className="agent-action-primary"
                  onClick={() => canBook && onBookDoctor ? onBookDoctor(doctor, copy.cards.book) : onQuickCommand(command, canBook ? copy.cards.book : copy.cards.invite)}
                >
                  {canBook ? copy.cards.book : copy.cards.invite}
                </button>
                {(doctor?.raw?.website || doctor?.profileUrl) && (
                  <button type="button" onClick={() => onOpenUrl(doctor.raw?.website || doctor.profileUrl, true)}>
                    {copy.cards.viewProfile}
                  </button>
                )}
              </div>
            </article>
          );
        }}
      />
    </section>
  );
}

function PlaceResultCards({ data, copy, locale, onOpenUrl }) {
  const places = Array.isArray(data?.places) ? data.places : [];
  if (!places.length) return null;

  return (
    <section className="agent-data-section">
      <SectionTitle title={data?.category_label || copy.cards.locations} count={places.length} />
      <ExpandableItems
        items={places}
        initialCount={10}
        className="agent-card-list agent-card-list--places"
        copy={copy}
        renderItem={(place, index) => {
          const distance = place?.distance ?? place?.distance_km;
          const mapsUrl = place?.google_maps_url || place?.maps_url || place?.actions?.find((action) => action?.type === "google_maps")?.url;
          const routeUrl = place?.route_url || place?.actions?.find((action) => action?.external === false)?.url;
          return (
            <article className="agent-result-card place" key={`${place?.id || place?.name}-${index}`}>
              <div className="agent-result-card__top">
                <div>
                  <h3>{place?.name || "Lieu médical"}</h3>
                  <p>{place?.category_label || place?.category || data?.category_label || ""}</p>
                </div>
                {distance != null && <span className="agent-pill is-primary">{formatNumber(distance)} km</span>}
              </div>
              {place?.address && <div className="agent-card-muted">{place.address}</div>}
              <div className="agent-chip-row">
                {place?.rating != null && <span className="agent-chip">{copy.cards.rating}: {place.rating}/5</span>}
                {place?.phone && <span className="agent-chip">{copy.cards.phone}: {place.phone}</span>}
              </div>
              <div className="agent-card-actions">
                {mapsUrl && <button type="button" className="agent-action-primary" onClick={() => onOpenUrl(mapsUrl, true)}>{copy.cards.maps}</button>}
                {routeUrl && <button type="button" onClick={() => onOpenUrl(routeUrl)}>{copy.cards.openPage}</button>}
              </div>
            </article>
          );
        }}
      />
    </section>
  );
}

function SlotResultCards({ data, copy, locale, onQuickCommand }) {
  const slots = Array.isArray(data?.appointment_slots) ? data.appointment_slots : [];
  const days = Array.isArray(data?.available_days) ? data.available_days : [];
  const periods = Array.isArray(data?.available_periods) ? data.available_periods : [];
  const showBookingCalendar = Boolean(data?.booking_calendar);
  if (!slots.length && !days.length && !periods.length) return null;

  return (
    <section className="agent-data-section">
      {!!slots.length && (
        <>
          <SectionTitle
            title={copy.cards.slots}
            count={slots.length}
            subtitle={formatDate(data?.date, locale)}
          />
          <ExpandableItems
            items={slots}
            initialCount={12}
            className="agent-slot-grid"
            copy={copy}
            renderItem={(slot, index) => {
              const label = slot?.label || timeRangeLabel(slot) || slot?.startTime || `#${index + 1}`;
              const command = slot?.command || `confirmer rendez-vous créneau #${slot?.index || slot?.id || index + 1}`;
              return (
                <button
                  type="button"
                  className="agent-slot-button"
                  key={`${slot?.id || index}-${label}`}
                  onClick={() => onQuickCommand(command, label)}
                >
                  {label}
                </button>
              );
            }}
          />
        </>
      )}

      {!!days.length && (
        <>
          <SectionTitle title={copy.cards.availableDays} count={days.length} />
          {showBookingCalendar ? (
            <AvailableDaysCalendar days={days} copy={copy} locale={locale} onQuickCommand={onQuickCommand} />
          ) : (
            <ExpandableItems
              items={days}
              initialCount={10}
              className="agent-card-list agent-card-list--compact"
              copy={copy}
              renderItem={(day, index) => {
                const label = day?.label || formatDate(day?.date, locale) || day?.date || `Jour #${index + 1}`;
                const count = day?.slots_count || day?.count;
                const command = day?.command || `voir les créneaux du ${day?.date || label}`;
                return (
                  <button type="button" className="agent-mini-card" key={`${day?.date || index}`} onClick={() => onQuickCommand(command, `${copy.cards.viewSlots} ${label}`)}>
                    <strong>{label}</strong>
                    {count != null && <span>{count} {copy.cards.slots.toLowerCase()}</span>}
                  </button>
                );
              }}
            />
          )}
        </>
      )}

      {!!periods.length && (
        <>
          <SectionTitle title={copy.cards.availablePeriods} count={periods.length} />
          <ExpandableItems
            items={periods}
            initialCount={8}
            className="agent-card-list agent-card-list--compact"
            copy={copy}
            renderItem={(period, index) => {
              const label = period?.title || period?.label || `${formatDate(period?.start_date, locale)} - ${formatDate(period?.end_date, locale)}`;
              const command = period?.command || `voir les créneaux pour la période ${period?.period_id || period?.id || index + 1}`;
              return (
                <button type="button" className="agent-mini-card" key={`${period?.period_id || period?.id || index}`} onClick={() => onQuickCommand(command, label)}>
                  <strong>{label}</strong>
                  {(period?.available_slots_count || period?.slots_count) != null && <span>{period.available_slots_count || period.slots_count} {copy.cards.slots.toLowerCase()}</span>}
                </button>
              );
            }}
          />
        </>
      )}
    </section>
  );
}

function AvailableDaysCalendar({ days, copy, locale, onQuickCommand }) {
  const normalizedDays = days
    .map((day) => normalizeAvailableDay(day))
    .filter(Boolean)
    .sort((a, b) => a.date.localeCompare(b.date));
  const months = buildCalendarMonths(normalizedDays, locale);
  if (!months.length) return null;

  return (
    <div className="agent-calendar-stack">
      {months.map((month) => (
        <div className="agent-calendar" key={month.key}>
          <div className="agent-calendar__header">
            <strong>{month.label}</strong>
            <span>{month.availableCount} {copy.cards.available.toLowerCase()}</span>
          </div>
          <div className="agent-calendar__weekdays" aria-hidden="true">
            {calendarWeekdayLabels(locale).map((label) => <span key={`${month.key}-${label}`}>{label}</span>)}
          </div>
          <div className="agent-calendar__grid">
            {month.cells.map((cell) => {
              if (cell.empty) return <span className="agent-calendar-day is-empty" key={cell.key} aria-hidden="true" />;
              const day = cell.item;
              const available = Boolean(day);
              const label = day?.label || formatDate(cell.date, locale) || cell.date;
              const count = day?.slots_count ?? day?.count;
              const command = day?.command || calendarDayCommand(cell.date, locale);
              return (
                <button
                  type="button"
                  className={`agent-calendar-day ${available ? "is-available" : "is-disabled"} ${cell.isToday ? "is-today" : ""}`}
                  key={cell.key}
                  disabled={!available}
                  onClick={() => available && onQuickCommand(command, `${copy.cards.viewSlots} ${label}`)}
                  aria-label={available ? `${copy.cards.viewSlots} ${label}` : label}
                >
                  <strong>{cell.dayNumber}</strong>
                  {available && count != null && <small>{count}</small>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function BookingConfirmationCard({ data, copy, locale, onQuickCommand }) {
  const slot = data?.selected_slot;
  if (!data?.requires_confirmation) return null;
  const label = slot
    ? dateTimeRangeLabel(slot, locale) || slot?.label || ""
    : data?.confirmation_description || "";
  const command = data?.confirmation_command || (locale === "en" ? "yes I confirm" : "oui je confirme");
  const buttonLabel = locale === "en" ? "Confirm" : locale === "ar" ? "تأكيد" : "Confirmer";

  return (
    <section className="agent-data-section">
      <div className="agent-booking-confirmation">
        <div>
          <strong>{data?.confirmation_title || copy.cards.confirmation}</strong>
          {label && <span>{label}</span>}
        </div>
        <button type="button" className="agent-action-primary" onClick={() => onQuickCommand(command, buttonLabel)}>
          {buttonLabel}
        </button>
      </div>
    </section>
  );
}

function AppointmentResultCards({ data, copy, locale, role, onQuickCommand, onManageAppointment }) {
  const appointments = Array.isArray(data?.appointments) ? data.appointments : [];
  if (!appointments.length) return null;
  const showActions = !data?.requires_confirmation;

  return (
    <section className="agent-data-section">
      <SectionTitle
        title={copy.cards.appointments}
        count={appointments.length}
      />
      <ExpandableItems
        items={appointments}
        initialCount={10}
        className="agent-card-list"
        copy={copy}
        renderItem={(appointment, index) => {
          const id = appointment?.id || appointment?.appointmentId || index + 1;
          const fallbackTitle = normalizeLocale(locale) === "ar" ? `موعد #${id}` : normalizeLocale(locale) === "en" ? `Appointment #${id}` : `Rendez-vous #${id}`;
          const patientDisplayName = appointmentPatientDisplayName(appointment);
          const doctorDisplayName = appointmentDoctorDisplayName(appointment);
          const appointmentTitle = readableAppointmentTitle(appointment?.title);
          const title = role === "doctor"
            ? patientDisplayName || appointmentTitle || fallbackTitle
            : doctorDisplayName || appointmentTitle || patientDisplayName || fallbackTitle;
          const time = appointment?.timeLabel || dateTimeRangeLabel(appointment, locale);
          const status = appointment?.status || appointment?.etat || "";
          const statusKey = String(status || "").toLowerCase();
          const isActionable = !["cancelled", "canceled", "rejected", "completed"].includes(statusKey);
          return (
            <article className="agent-result-card appointment" key={`${id}-${index}`}>
              <div className="agent-result-card__top">
                <div>
                  <h3>{title}</h3>
                  {time && <p>{time}</p>}
                </div>
                {status && <span className="agent-pill is-muted">{status}</span>}
              </div>
              {showActions && isActionable && <div className="agent-card-actions">
                {role === "doctor" ? (
                  <>
                    <button type="button" className="agent-action-primary" onClick={() => onQuickCommand(`accepter rendez-vous #${id}`, copy.cards.accept)}>{copy.cards.accept}</button>
                    <button type="button" onClick={() => onQuickCommand(`refuser rendez-vous #${id}`, copy.cards.reject)}>{copy.cards.reject}</button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="agent-action-primary"
                      onClick={() => onManageAppointment
                        ? onManageAppointment(appointment, PATIENT_RESCHEDULE_APPOINTMENT, copy.cards.reschedule)
                        : onQuickCommand(`modifier rendez-vous #${id}`, copy.cards.reschedule)}
                    >
                      {copy.cards.reschedule}
                    </button>
                    <button
                      type="button"
                      onClick={() => onManageAppointment
                        ? onManageAppointment(appointment, PATIENT_CANCEL_APPOINTMENT, copy.cards.cancel)
                        : onQuickCommand(`annuler rendez-vous #${id}`, copy.cards.cancel)}
                    >
                      {copy.cards.cancel}
                    </button>
                  </>
                )}
              </div>}
            </article>
          );
        }}
      />
    </section>
  );
}

function AvailabilityResultCards({ data, copy, locale, onQuickCommand }) {
  const slots = Array.isArray(data?.slots) ? data.slots : [];
  if (!slots.length) return null;
  const availableCount = slots.filter(isAvailableSlot).length;

  return (
    <section className="agent-data-section">
      <SectionTitle
        title={copy.cards.availability}
        count={slots.length}
        subtitle={`${availableCount} ${copy.cards.available.toLowerCase()}`}
      />
      <ExpandableItems
        items={slots}
        initialCount={16}
        className="agent-card-list agent-card-list--compact"
        copy={copy}
        renderItem={(slot, index) => {
          const available = isAvailableSlot(slot);
          const label = slot?.label || timeRangeLabel(slot) || `#${slot?.id || index + 1}`;
          const reason = slotReason(slot, copy);
          const canInspectSlots = !slot?.isGeneratedSlot;
          const blockLabel = normalizedBlockLabel(locale);
          return (
            <article
              className={`agent-availability-card ${available ? "is-available" : "is-blocked"}`}
              key={`${slot?.id || index}-${label}`}
            >
              <strong>{label}</strong>
              <span>{available ? copy.cards.available : `${copy.cards.unavailable} - ${reason}`}</span>
              {!available && slotReasonDetail(slot) && <small>{slotReasonDetail(slot)}</small>}
              <div className="agent-availability-actions">
                {canInspectSlots && (
                  <button
                    type="button"
                    className="agent-action-secondary"
                    onClick={() => onQuickCommand(availabilitySlotsCommand(slot, data, label), `${copy.cards.viewSlots} ${label}`)}
                  >
                    {copy.cards.viewSlots}
                  </button>
                )}
                {available && (
                  <button
                    type="button"
                    className="agent-action-block"
                    onClick={() => onQuickCommand(availabilityBlockCommand(slot, data, index), `${blockLabel} ${label}`)}
                  >
                    {blockLabel}
                  </button>
                )}
              </div>
            </article>
          );
        }}
      />
    </section>
  );
}

function EventResultCards({ data, copy, locale, canDeleteEvents, eventScope, onOpenUrl, onDeleteEvent }) {
  const [confirmingId, setConfirmingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [errorKey, setErrorKey] = useState("");
  const busyRef = useRef(false);
  const scopeRef = useRef(eventScope);
  scopeRef.current = eventScope;
  const labels = eventCancelText(locale);
  useEffect(() => {
    setConfirmingId(null);
    setBusyId(null);
    setErrorKey("");
  }, [eventScope]);

  const confirmDelete = async (eventId) => {
    if (busyRef.current || !onDeleteEvent) return;
    busyRef.current = true;
    const requestScope = eventScope;
    setBusyId(eventId);
    setErrorKey("");
    try {
      await onDeleteEvent(eventId, data);
      if (scopeRef.current === requestScope) setConfirmingId(null);
    } catch (error) {
      if (scopeRef.current === requestScope) {
        setErrorKey(error?.message === "EVENTS_REFRESH_FAILED" ? "refresh" : eventDeleteFailure(error));
      }
    } finally {
      busyRef.current = false;
      if (scopeRef.current === requestScope) setBusyId(null);
    }
  };

  const events = Array.isArray(data?.events) ? data.events : data?.event ? [data.event] : [];
  if (!events.length) return null;

  return (
    <section className="agent-data-section">
      <SectionTitle title={copy.cards.events} count={events.length} />
      <ExpandableItems
        items={events}
        initialCount={10}
        className="agent-card-list"
        copy={copy}
        renderItem={(event, index) => {
          const eventId = structuredEventId(event);
          const isConfirming = confirmingId === eventId;
          return (
            <article className="agent-result-card event" key={`${eventId || index}`}>
              <div className="agent-result-card__top">
                <div>
                  <h3>{event?.title || event?.name || "Événement"}</h3>
                  <p>{event?.timeLabel || dateTimeRangeLabel(event, locale)}</p>
                </div>
                {event?.type && <span className="agent-pill is-muted">{event.type}</span>}
              </div>
              {event?.reason && <div className="agent-card-muted">{event.reason}</div>}
              {(event?.url || (canDeleteEvents && eventId)) && (
                <div className="agent-card-actions">
                  {event?.url && <button type="button" onClick={() => onOpenUrl(event.url)}>{copy.open}</button>}
                  {canDeleteEvents && eventId && (
                    <button
                      type="button"
                      onClick={() => { setConfirmingId(eventId); setErrorKey(""); }}
                      disabled={busyId !== null}
                    >
                      {labels.cancel}
                    </button>
                  )}
                </div>
              )}
              {isConfirming && (
                <div className="agent-event-confirm" role="group" aria-label={labels.prompt}>
                  <p>{labels.prompt} <strong>{event?.title || event?.name}</strong> — {event?.timeLabel || dateTimeRangeLabel(event, locale)}</p>
                  <div className="agent-card-actions">
                    <button type="button" className="agent-action-primary" disabled={busyId !== null} onClick={() => confirmDelete(eventId)}>
                      {busyId === eventId ? labels.working : labels.confirm}
                    </button>
                    <button type="button" disabled={busyId !== null} onClick={() => { setConfirmingId(null); setErrorKey(""); }}>{labels.back}</button>
                  </div>
                  {errorKey && <p className="agent-event-error" role="alert">{labels[errorKey]}</p>}
                </div>
              )}
            </article>
          );
        }}
      />
    </section>
  );
}

function MedicationResultCards({ data, copy, locale, onOpenUrl }) {
  const medications = Array.isArray(data?.medications) ? data.medications : [];
  const active = Array.isArray(data?.active_medications) ? data.active_medications : [];
  const inactive = Array.isArray(data?.inactive_medications) ? data.inactive_medications : [];
  const all = medications.length ? medications : [...active, ...inactive];
  if (!all.length) return null;

  return (
    <section className="agent-data-section">
      <SectionTitle title={copy.cards.medications} count={all.length} />
      <ExpandableItems
        items={all}
        initialCount={10}
        className="agent-card-list"
        copy={copy}
        renderItem={(medication, index) => (
          <article className="agent-result-card medication" key={`${medication?.id || medication?.name || index}`}>
            <div className="agent-result-card__top">
              <div>
                <h3>{medication?.name || medication?.nom || medication?.title || "Médicament"}</h3>
                <p>{[medication?.dose, medication?.dosage, medication?.frequency, medication?.schedule].filter(Boolean).join(" - ")}</p>
              </div>
              {(medication?.status || medication?.etat) && <span className="agent-pill is-muted">{medication.status || medication.etat}</span>}
            </div>
            <div className="agent-chip-row">
              {medication?.startDate && <span className="agent-chip">{formatDate(medication.startDate, locale)}</span>}
              {Array.isArray(medication?.times) && medication.times.map((time) => <span className="agent-chip" key={time}>{time}</span>)}
            </div>
          </article>
        )}
      />
      {data?.medications_route && (
        <div className="agent-card-actions">
          <button type="button" className="agent-action-primary" onClick={() => onOpenUrl(data.medications_route)}>
            {copy.cards.openPage}
          </button>
        </div>
      )}
    </section>
  );
}

function MedicalRecordResultCards({ data, copy, locale, onOpenUrl }) {
  const patient = data?.patient && typeof data.patient === "object" ? data.patient : null;
  const medicalInfo = data?.medical_info && typeof data.medical_info === "object" ? data.medical_info : null;
  const record = data?.medical_record && typeof data.medical_record === "object" ? data.medical_record : null;
  const snapshot = record?.snapshot && typeof record.snapshot === "object" ? record.snapshot : record;
  const allergies = Array.isArray(data?.allergies) && data.allergies.length ? data.allergies : record?.allergies || [];
  const vaccines = Array.isArray(data?.vaccines) && data.vaccines.length ? data.vaccines : record?.vaccinations || [];
  const documentCounts = data?.document_counts && typeof data.document_counts === "object" ? data.document_counts : null;
  const hasRecord = patient || medicalInfo || record || allergies.length || vaccines.length || data?.bmi || documentCounts;
  if (!hasRecord) return null;

  const patientName = patient?.name || patient?.fullName || patient?.username || "";
  const metrics = compactMetrics([
    ["Age", data?.age || patient?.age],
    ["Genre", patient?.gender || patient?.genre || patient?.sex],
    [copy.cards.phone, patient?.phone || patient?.telephone],
    ["Groupe sanguin", medicalInfo?.groupeSanguin || snapshot?.bloodType || snapshot?.bloodGroup],
    ["Rhesus", snapshot?.rhFactor],
    ["Maladies chroniques", snapshot?.maladiesChroniques],
    ["Pathologie", snapshot?.pathologie === true ? "Oui" : snapshot?.pathologie === false ? "Non" : null],
    ["Donneur de sang", snapshot?.donneurSang === true ? "Oui" : snapshot?.donneurSang === false ? "Non" : null],
    ["Mutuelle", snapshot?.nomMutuelle],
    ["Assurance", Array.isArray(snapshot?.insurance) ? snapshot.insurance.join(" ; ") : null],
    ["Poids", data?.weight || data?.bmi?.poidsKg || record?.weight],
    ["Taille", data?.height || data?.bmi?.tailleCm || record?.height],
    ["IMC", data?.bmi?.imc || data?.bmi],
    ["Consultations", data?.consultation_count],
  ]);

  return (
    <section className="agent-data-section">
      <SectionTitle title={patientName ? `${copy.cards.patientRecord} - ${patientName}` : copy.cards.medicalInfo} />
      {!!metrics.length && (
        <div className="agent-metric-grid">
          {metrics.map(([label, value]) => (
            <div className="agent-metric-card" key={label}>
              <span>{label}</span>
              <strong>{formatMetricValue(value, locale)}</strong>
            </div>
          ))}
        </div>
      )}

      {!!allergies.length && (
        <MiniList title="Allergies" items={medicalEntryTexts(allergies, ["name", "value", "label", "medicationName", "foodName", "allergen", "severityLevel"])} copy={copy} />
      )}
      {!!vaccines.length && (
        <MiniList title="Vaccins" items={medicalEntryTexts(vaccines, ["vaccineName", "name", "vaccine", "label", "administrationDate"])} copy={copy} />
      )}
      {record && [
        ["family_history", "Antecedents familiaux", ["relation", "condition"]],
        ["past_illnesses", "Maladies anterieures", ["illnessName", "diagnosisDate", "notes"]],
        ["hospitalizations", "Hospitalisations", ["cause", "hospitalizationDate", "clinicName", "notes"]],
        ["infectious_diseases", "Maladies infectieuses", ["diseaseName", "diagnosisDate", "notes"]],
        ["diagnoses", "Diagnostics", ["diagnosisName", "diagnosisDate", "status"]],
        ["surgical_history", "Historique chirurgie et allergies", ["item", "type", "date"]],
        ["personal_history", "Antecedents personnels", ["type", "value", "status"]],
        ["symptoms", "Symptomes", ["symptomName", "startDate", "description"]],
      ].map(([key, title, fields]) => {
        const items = medicalEntryTexts(record[key], fields);
        return items.length ? <MiniList key={key} title={title} items={items} copy={copy} /> : null;
      })}
      {documentCounts && (
        <DocumentCounts counts={documentCounts} copy={copy} data={data} onOpenUrl={onOpenUrl} />
      )}
    </section>
  );
}

function medicalEntryTexts(entries, fields) {
  if (!Array.isArray(entries)) return [];
  return entries.map((entry) => {
    if (typeof entry === "string") return entry;
    if (!entry || typeof entry !== "object") return "";
    return fields.map((field) => entry[field]).filter((value) => value !== undefined && value !== null && value !== "").join(" ; ");
  }).filter(Boolean);
}

function ConfirmationResultCard({ data, copy }) {
  if (data?.requires_confirmation) return null;
  if (hasPrimaryVisualResult(data)) return null;
  const hasConfirmation = data?.created || data?.updated || data?.deleted || data?.payload || data?.selected_slot;
  if (!hasConfirmation) return null;
  return (
    <section className="agent-data-section">
      <div className="agent-confirmation-card">
        <strong>{copy.cards.confirmation}</strong>
        <span>{data?.updated ? "Mise à jour effectuée" : data?.deleted ? "Suppression effectuée" : "Action effectuée"}</span>
      </div>
    </section>
  );
}

function hasPrimaryVisualResult(data) {
  return Boolean(
    countArray(data?.doctors) ||
    countArray(data?.places) ||
    countArray(data?.appointment_slots) ||
    countArray(data?.available_days) ||
    countArray(data?.available_periods) ||
    countArray(data?.appointments) ||
    countArray(data?.slots) ||
    countArray(data?.events) ||
    countArray(data?.medications) ||
    countArray(data?.active_medications) ||
    countArray(data?.inactive_medications) ||
    data?.appointment ||
    data?.event ||
    data?.medical_info ||
    data?.medical_record ||
    data?.patient ||
    data?.document_counts ||
    data?.bmi
  );
}

function SectionTitle({ title, count, subtitle }) {
  return (
    <div className="agent-section-title">
      <div>
        <h4>{title}</h4>
        {subtitle && <span>{subtitle}</span>}
      </div>
      {count != null && <em>{count}</em>}
    </div>
  );
}

function displayedCountLabel(total, visible, locale) {
  if (!total || total <= visible) return "";
  const normalized = normalizeLocale(locale);
  if (normalized === "en") return `Showing first ${visible}`;
  if (normalized === "ar") return `عرض أول ${visible}`;
  return `${visible} premiers affiches`;
}

function MiniList({ title, items, copy }) {
  return (
    <div className="agent-mini-list">
      <strong>{title}</strong>
      <ExpandableItems
        items={items}
        initialCount={12}
        className="agent-chip-row"
        copy={copy}
        renderItem={(item, index) => (
          <span className="agent-chip" key={`${item}-${index}`}>{item}</span>
        )}
      />
    </div>
  );
}

function DocumentCounts({ counts, data, copy, onOpenUrl }) {
  const entries = Object.entries(counts || {}).filter(([, count]) => Number(count) > 0);
  if (!entries.length) return null;
  return (
    <div className="agent-mini-list">
      <strong>{copy.cards.documents}</strong>
      <div className="agent-card-list agent-card-list--compact">
        {entries.map(([type, count]) => {
          const url = data?.document_type_route || data?.documents_route;
          return (
            <button type="button" className="agent-mini-card" key={type} onClick={() => url && onOpenUrl(url)}>
              <strong>{humanizeKey(type)}</strong>
              <span>{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function compactMetrics(metrics) {
  return metrics.filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "" && String(value) !== "[object Object]");
}

function formatMetricValue(value, locale) {
  if (value == null) return "";
  if (typeof value === "object") {
    if (value.imc) return value.imc;
    if (value.value) return value.value;
    if (value.name) return value.name;
    return "";
  }
  if (String(value).match(/^\d{4}-\d{2}-\d{2}/)) return formatDate(value, locale);
  return String(value);
}

function isAvailableSlot(slot) {
  const status = String(slot?.status || slot?.statusLabel || "").toLowerCase();
  if (slot?.isAvailable === false || slot?.available === false) return false;
  if (status.includes("bloqu") || status.includes("indisponible") || status.includes("unavailable")) return false;
  return true;
}

function slotReason(slot, copy) {
  const raw = String(slot?.unavailableReason || slot?.reasonLabel || slot?.reason || "").toLowerCase();
  if (raw.includes("appointment") || raw.includes("rdv") || raw.includes("rendez")) return copy.cards.appointments;
  if (raw.includes("event") || raw.includes("even") || raw.includes("évén")) return copy.cards.events;
  if (raw.includes("bloqu") || raw.includes("block")) return "Créneau bloqué";
  return raw ? humanizeKey(raw) : copy.cards.reason;
}

function slotReasonDetail(slot) {
  return slot?.unavailableLabel || slot?.reasonDetail || slot?.busyLabel || slot?.patientName || slot?.eventTitle || "";
}

function timeRangeLabel(item) {
  const start = formatTime(item?.startTime || item?.start_time || item?.start);
  const end = formatTime(item?.endTime || item?.end_time || item?.end);
  return [start, end].filter(Boolean).join(" - ");
}

function availabilityDateValue(slot, data) {
  const value = slot?.date || slot?.startDate || slot?.start_date || slot?.startTime || slot?.start || data?.start_date || data?.date;
  const text = String(value || "");
  const match = text.match(/\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : text.slice(0, 10);
}

function availabilityTimeParts(slot) {
  return {
    start: formatTime(slot?.startTime || slot?.start_time || slot?.start),
    end: formatTime(slot?.endTime || slot?.end_time || slot?.end),
  };
}

function availabilitySlotsCommand(slot, data, label) {
  const date = availabilityDateValue(slot, data);
  const { start, end } = availabilityTimeParts(slot);
  if (date && start && end) return `affiche les slots disponibles du ${date} de ${start} a ${end}`;
  if (date) return `affiche les slots disponibles du ${date}`;
  return `affiche les slots disponibles ${label}`;
}

function availabilityBlockCommand(slot, data, index) {
  const generated = Boolean(slot?.isGeneratedSlot);
  const sourceId = slot?.availabilitySlotId || slot?.sourceSlotId;
  const rawId = slot?.id;
  const numericId = rawId != null && /^\d+$/.test(String(rawId)) ? rawId : null;
  const slotId = sourceId || numericId;
  if (!generated && slotId) return `bloquer le creneau #${slotId}`;

  const date = availabilityDateValue(slot, data);
  const { start, end } = availabilityTimeParts(slot);
  if (date && start && end) return `bloquer le creneau du ${date} de ${start} a ${end}`;
  return `bloquer le creneau #${rawId || index + 1}`;
}

function normalizedBlockLabel(locale) {
  if (locale === "en") return "Block";
  if (locale === "ar") return "حظر";
  return "Bloquer";
}

function dateTimeRangeLabel(item, locale) {
  const date = formatDate(item?.date || item?.startTime || item?.start || item?.start_date, locale);
  const range = timeRangeLabel(item);
  return [date, range].filter(Boolean).join(" · ");
}

function normalizeAvailableDay(day) {
  const match = String(day?.date || day?.day || day?.startDate || "").match(/\d{4}-\d{2}-\d{2}/);
  if (!match) return null;
  const parsed = parseCalendarDate(match[0]);
  if (!parsed) return null;
  return {
    ...day,
    date: match[0],
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    dayNumber: parsed.getDate(),
  };
}

function buildCalendarMonths(days, locale) {
  const byDate = new Map(days.map((day) => [day.date, day]));
  const monthKeys = Array.from(new Set(days.map((day) => `${day.year}-${String(day.month).padStart(2, "0")}`)));
  const today = calendarIso(new Date());
  const weekStart = normalizeLocale(locale) === "en" ? 0 : 1;

  return monthKeys.map((key) => {
    const [year, month] = key.split("-").map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const leading = (firstDay.getDay() - weekStart + 7) % 7;
    const cells = Array.from({ length: leading }, (_, index) => ({
      key: `${key}-empty-${index}`,
      empty: true,
    }));

    for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber += 1) {
      const iso = calendarIso(new Date(year, month - 1, dayNumber));
      cells.push({
        key: iso,
        date: iso,
        dayNumber,
        item: byDate.get(iso),
        isToday: iso === today,
      });
    }

    return {
      key,
      label: firstDay.toLocaleDateString(locale === "ar" ? "ar-TN" : locale === "en" ? "en-US" : "fr-FR", {
        month: "long",
        year: "numeric",
      }),
      availableCount: days.filter((day) => day.year === year && day.month === month).length,
      cells,
    };
  });
}

function calendarWeekdayLabels(locale) {
  if (normalizeLocale(locale) === "en") return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  if (normalizeLocale(locale) === "ar") return ["ن", "ث", "ر", "خ", "ج", "س", "ح"];
  return ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
}

function calendarDayCommand(dateValue, locale) {
  return normalizeLocale(locale) === "en"
    ? `book appointment on ${dateValue}`
    : `prendre rendez-vous le ${dateValue}`;
}

function parseCalendarDate(value) {
  const dateValue = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(dateValue.getTime()) ? null : dateValue;
}

function calendarIso(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function preferredRecordingMimeType() {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
    "audio/wav",
  ].find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

function extensionFromMime(mimeType) {
  const value = String(mimeType || "").toLowerCase();
  if (value.includes("mp4")) return "m4a";
  if (value.includes("mpeg") || value.includes("mp3")) return "mp3";
  if (value.includes("wav")) return "wav";
  if (value.includes("ogg")) return "ogg";
  return "webm";
}

function stopMediaStream(stream) {
  if (!stream) return;
  stream.getTracks?.().forEach((track) => track.stop());
}

const UUID_LIKE_TEXT = /\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/i;

function cleanDisplayName(value) {
  const text = String(value || "").trim().replace(/\s+/g, " ");
  if (!text || UUID_LIKE_TEXT.test(text)) return "";
  if (/^(doctor|docteur|medecin|médecin|patient|rendez-vous|appointment|موعد)\s*#?/i.test(text)) return "";
  return text;
}

function personDisplayName(person = {}) {
  if (!person || typeof person !== "object") return "";

  const fullName = cleanDisplayName(
    person.fullName ||
    person.displayName ||
    person.name ||
    person.username
  );
  if (fullName) return fullName;

  const firstName = String(person.firstName || person.firstname || person.prenom || "").trim();
  const lastName = String(person.lastName || person.lastname || person.nom || "").trim();
  const composedName = cleanDisplayName(`${firstName} ${lastName}`.trim());
  if (composedName) return composedName;

  return cleanDisplayName(person.identifier || person.email);
}

function readableAppointmentTitle(title) {
  return cleanDisplayName(title);
}

function appointmentPatientDisplayName(appointment = {}) {
  return (
    cleanDisplayName(appointment.patientDisplayName) ||
    cleanDisplayName(appointment.patientFullName) ||
    cleanDisplayName(appointment.patientName) ||
    personDisplayName(appointment.patient) ||
    personDisplayName(appointment.user) ||
    personDisplayName(appointment.patientUser)
  );
}

function appointmentDoctorDisplayName(appointment = {}) {
  return (
    cleanDisplayName(appointment.doctorDisplayName) ||
    cleanDisplayName(appointment.doctorFullName) ||
    cleanDisplayName(appointment.doctorName) ||
    cleanDisplayName(appointment.medecinName) ||
    personDisplayName(appointment.doctor) ||
    personDisplayName(appointment.medecin)
  );
}

function formatDate(value, locale = "fr") {
  if (!value) return "";
  try {
    return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString(locale === "ar" ? "ar-TN" : locale === "en" ? "en-US" : "fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function formatTime(value) {
  if (!value) return "";
  const text = String(value).trim();
  const explicitTime = text.match(/[T\s](\d{1,2}):(\d{2})(?::\d{2})?/) || text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (explicitTime) return `${explicitTime[1].padStart(2, "0")}:${explicitTime[2]}`;
  const genericTime = text.match(/(?:^|[^\d])(\d{1,2}):(\d{2})(?::\d{2})?(?:[^\d]|$)/);
  return genericTime ? `${genericTime[1].padStart(2, "0")}:${genericTime[2]}` : "";
}

function formatNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value || "");
  return number.toFixed(2).replace(/\.?0+$/, "");
}

function humanizeKey(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}
